import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faCircleInfo, faThumbtack, faCalendarDays } from "@fortawesome/free-solid-svg-icons";
import { buildUrl, fetchJson } from "../shared/api";
import { Markdown, normalizeMarkdown } from "../shared/markdown";
import { groupReleaseNotesByMajor, parseReleaseDate, flagBadge, effectiveFlag } from "../shared/releaseNotes";
import { isNumericMajorKey, compareVersionsDesc } from "../shared/version";
import type { IdmInfo, ModuleInfo, ReleaseNote, Version } from "../shared/types/api";
import { useTranslation } from "react-i18next";
import { useSettings } from "../shared/hooks/useSettings";

type GroupedReleaseNotes = Record<string, ReleaseNote[]>;
type OpenState = Record<string, boolean>;

function ModuleDetail() {
    const { t, i18n } = useTranslation();
    const { settings } = useSettings();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const repository = params.get("repository");
    // major is not used here, but keep for parity
    //const major = params.get("major");

    const navigate = useNavigate();

    const [latestVersion, setLatestVersion] = useState<Version | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [module, setModule] = useState<ModuleInfo>();

    const [groupedResults, setGroupedResults] = useState<GroupedReleaseNotes>({});
    const [openMajors, setOpenMajors] = useState<OpenState>({});
    const [openVersions, setOpenVersions] = useState<OpenState>({});
    const [descriptionOpen, setDescriptionOpen] = useState<boolean | null>(null);
    const [idms, setIdms] = useState<IdmInfo[]>([]);

    // the default open/closed state comes from user settings; explicit toggles win
    const isDescriptionOpen = descriptionOpen ?? settings.descriptionsExpanded;
    const isVersionOpen = (tag: string) => openVersions[tag] ?? settings.descriptionsExpanded;

    useEffect(() => {
        if (!repository) {
            navigate("/");
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setError(null);

        (async () => {
            try {
                const [notes, moduleInfo, versions, idmList] = await Promise.all([
                    fetchJson<ReleaseNote[]>(
                        buildUrl("/release-note/range", { repository, from: "", to: "" })
                    ),
                    fetchJson<ModuleInfo>(
                        buildUrl("/module", { module: repository })
                    ),
                    fetchJson<Version[]>(
                        buildUrl("/version/module", { repository: repository })
                    ),
                    fetchJson<IdmInfo[]>(buildUrl("/idm/all")),
                ]);
                // The latest version is the highest one (the entry at the top of
                // the table below), not just the last element returned by the API.
                const latest = versions && versions.length > 0
                    ? [...versions].sort((a, b) =>
                        compareVersionsDesc(a.tag || a.moduleVersion, b.tag || b.moduleVersion)
                    )[0]
                    : null;

                if (latest) {
                    const latestTag = latest.tag || latest.moduleVersion;
                    const latestNote = notes.find(n => n.tag_name === latestTag);
                    if (latestNote?.released_at) {
                        latest.date = latestNote.released_at;
                    }
                    setLatestVersion(latest);
                } else {
                    setLatestVersion(null);
                }

                // index existing notes by tag_name for fast lookup
                const noteByTag = new Map<string, ReleaseNote>();

                notes.forEach(note => {
                    note.repository = repository;
                    note.description = note.description ?? "";
                    noteByTag.set(note.tag_name, note);
                });

                // ensure every version has a corresponding note
                versions.forEach(v => {
                    const tag = v.tag || v.moduleVersion;
                    if (!tag) return;

                    const existing = noteByTag.get(tag);

                    if (existing) {
                        // enrich existing note
                        (existing as any).idmDependency = v.parentVersion;
                        (existing as any).maxIdM = v.maxIdM;
                    } else {
                        // create synthetic note for missing version
                        const synthetic: ReleaseNote = {
                            tag_name: tag,
                            description: "",
                            released_at: v.date,
                            repository: repository,
                            mismatchWithTag: v.mismatchWithTag
                        } as ReleaseNote;

                        (synthetic as any).idmDependency = v.parentVersion;
                        (synthetic as any).maxIdM = v.maxIdM;

                        notes.push(synthetic);
                        noteByTag.set(tag, synthetic);
                    }
                });

                if (cancelled) return;

                setModule(moduleInfo);
                setIdms(idmList);

                const groups = groupReleaseNotesByMajor(notes);
                setGroupedResults(groups);

                const expandedMajors: OpenState = {};
                Object.keys(groups).forEach((m) => {
                    expandedMajors[m] = true;
                });
                setOpenMajors(expandedMajors);
            } catch (err: unknown) {
                const message = err instanceof Error ? err.message : t("common.error");
                if (!cancelled) setError(message);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [repository, navigate, t, i18n.language]);

    const moduleDescription = useMemo(() => {
        return normalizeMarkdown(module?.description ?? "");
    }, [module?.description]);

    return (
        <div className="page-card">
            {module && (
                <h2 className="page-title">
                    <FontAwesomeIcon icon={faFileLines} /> {t("moduleDetail.title", { module: module.tableName })}
                </h2>
            )}

            {module && (
                <div className="detail-box detail-box--spaced">
                    <strong className="info-box__title">
                        {module.description && (
                            <span className="toggle-icon"
                                onClick={() => setDescriptionOpen(!isDescriptionOpen)}
                            >
                                {isDescriptionOpen ? "▼" : "▶"}
                            </span>
                        )}
                        <FontAwesomeIcon icon={faCircleInfo} /> {t("moduleDetail.info")}:
                    </strong>

                    {module.description && isDescriptionOpen && (
                        <div className="info-box__content">
                            {module.notLocalized && <p className="text-error"><strong>{t("moduleDetail.notLocalizedDescription", { language: module.fallbackLanguage })}</strong></p>}
                            <Markdown>{moduleDescription}</Markdown>
                        </div>
                    )}

                    {latestVersion && (
                        <div className="meta-block">
                            {module.dependencies && (
                                <p className="meta-line">
                                    <FontAwesomeIcon icon={faThumbtack} /> <strong>{t("moduleDetail.dependencies")}</strong>{" "}
                                    <span className="meta-highlight">
                                        {module.dependencies}
                                    </span>
                                </p>
                            )}
                            <p style={{ margin: 0 }}>
                                <FontAwesomeIcon icon={faThumbtack} /> <strong>{t("moduleDetail.latestVersion")}</strong>{" "}
                                <span style={{ color: "#2563eb" }}>
                                    {latestVersion.tag}
                                </span>
                            </p>
                            <p style={{ margin: 0 }}>
                                <FontAwesomeIcon icon={faCalendarDays} /> <strong>{t("moduleDetail.releaseDate")}</strong>{" "}
                                {parseReleaseDate(latestVersion.date)?.toLocaleDateString(i18n.language || "cs-CZ") ?? "—"}
                            </p>
                        </div>
                    )}
                </div>
            )}

            <div className="content-section">
                {isLoading && <p>{t("common.loading")}</p>}
                {error && <p className="text-error">{t("common.error")}: {error}</p>}

                {Object.keys(groupedResults).length > 0 && (
                    <div style={{ marginTop: "30px" }}>
                        <button className="btn-primary"
                            onClick={() => {
                                const collapsedVersions: OpenState = {};
                                Object.keys(groupedResults).forEach((m) => {
                                    groupedResults[m].forEach((rn) => {
                                        collapsedVersions[rn.tag_name] = false;
                                    });
                                });
                                setOpenVersions(collapsedVersions);
                            }}
                        >
                            {t("moduleDetail.collapseMinors")}
                        </button>

                        <button className="btn-primary"
                            onClick={() => {
                                const expandedVersions: OpenState = {};
                                Object.keys(groupedResults).forEach((m) => {
                                    groupedResults[m].forEach((rn) => {
                                        expandedVersions[rn.tag_name] = true;
                                    });
                                });
                                setOpenVersions(expandedVersions);
                            }}
                        >
                            {t("moduleDetail.expandMinors")}
                        </button>

                        {Object.keys(groupedResults)
                            .sort((a, b) => {
                                const aNumeric = isNumericMajorKey(a);
                                const bNumeric = isNumericMajorKey(b);

                                if (aNumeric && !bNumeric) return -1;
                                if (!aNumeric && bNumeric) return 1;

                                if (aNumeric && bNumeric) {
                                    return parseInt(b, 10) - parseInt(a, 10);
                                }

                                return a.localeCompare(b);
                            })
                            .map((majorKey) => (
                                <div key={majorKey} style={{ marginBottom: "20px" }}>
                                    <div className="major-header">
                                        {t("moduleDetail.majorHeader", { major: majorKey, count: groupedResults[majorKey].length })}
                                    </div>

                                    {openMajors[majorKey] && (
                                        <div className="major-content">
                                            {groupedResults[majorKey].map((rn) => (
                                                <div key={rn.tag_name} className="release-wrapper">
                                                    <div className="release-item">
                                                        <span className="toggle-icon"
                                                            onClick={() =>
                                                                setOpenVersions(prev => ({
                                                                    ...prev,
                                                                    [rn.tag_name]: !isVersionOpen(rn.tag_name),
                                                                }))
                                                            }
                                                        >
                                                            {isVersionOpen(rn.tag_name) ? "▼" : "▶"}
                                                        </span>

                                                        <span style={{ fontWeight: "bold", marginRight: "10px" }}>
                                                            {rn.tag_name}
                                                        </span>

                                                        {flagBadge(effectiveFlag(rn.flag, rn.tag_name)) && (
                                                            <span className="badge release-flag" style={{ marginRight: "10px" }}>
                                                                {flagBadge(effectiveFlag(rn.flag, rn.tag_name))}
                                                            </span>
                                                        )}

                                                        <span className="badge">
                                                            <FontAwesomeIcon icon={faCalendarDays} /> {parseReleaseDate(rn.released_at)?.toLocaleDateString(i18n.language || "cs-CZ") ?? "—"}
                                                        </span>

                                                        {rn.idmDependency && (() => {
                                                            const major = rn.idmDependency!.split(".")[0];
                                                            const idm = idms.find(i => i.tableName.includes(`${major}.`));
                                                            const cls = `badge ${rn.mismatchWithTag === "Yes" ? "badge--error" : "badge--primary"}`;
                                                            const label = rn.idmDependency === rn.maxIdM
                                                                ? `${rn.idmDependency}`
                                                                : `${rn.idmDependency} ≤ IdM ≤ ${rn.maxIdM}`;
                                                            return idm ? (
                                                                <Link
                                                                    to={`/idmdetail?repository=${encodeURIComponent(idm.nameOfRepo)}&major=${encodeURIComponent(major)}`}
                                                                    className={cls}
                                                                    style={{ textDecoration: "none" }}
                                                                >
                                                                    {label}
                                                                </Link>
                                                            ) : (
                                                                <span className={cls}>{label}</span>
                                                            );
                                                        })()}

                                                        {isVersionOpen(rn.tag_name) && (
                                                            <div className="detail-box detail-box--spaced">
                                                                {rn?.notLocalized && <p className="text-error"><strong>{t("moduleDetail.notLocalized", { language: rn.fallbackLanguage})}</strong></p> }
                                                                <p>
                                                                    <strong>{t("moduleDetail.releaseDate")}</strong>{" "}
                                                                    {parseReleaseDate(rn.released_at)?.toLocaleDateString(i18n.language || "cs-CZ") ?? "—"}
                                                                </p>

                                                                <div className="detail-box">
                                                                    <Markdown>
                                                                        {normalizeMarkdown(rn.description || "") || t("moduleDetail.noDescription")}
                                                                    </Markdown>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ModuleDetail;